import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const OpayHistoryPage = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        statusConfirmed: "text-yellow-400",
        cardHover: "hover:bg-[#1a0a00]",
        currencySymbol: "₵",
        flag: "🇬🇭",
        title: "Transaction History",
        emptyMsg: "No transactions yet — start sending Cedi!",
      }
    : {
        statusConfirmed: "text-green-400",
        cardHover: "hover:bg-gray-800",
        currencySymbol: "₦",
        flag: "🇳🇬",
        title: "Transaction History",
        emptyMsg: "No transactions found",
      };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await API.get("/bank/wallet/history");
        setTransactions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="animate-pulse">Loading Transaction History...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
        <button
          onClick={() => navigate(-1)}
          className="text-white text-lg font-bold"
        >
          ←
        </button>
        <h1 className="text-lg font-bold">{theme.title}</h1>
        <div className="w-6" /> {/* spacer */}
      </div>

      {/* History List */}
      {transactions.length > 0 ? (
        <div className="px-4 py-3 space-y-3">
          {transactions.map((tx, idx) => {
            // Use transaction's own currency if stored, otherwise country default
            const txSymbol = tx.currencySymbol || theme.currencySymbol;
            const txFlag = tx.country === "GH" ? "🇬🇭" : tx.country === "NG" ? "🇳🇳" : theme.flag;

            return (
              <div
                key={tx._id || idx}
                onClick={() => navigate(`/transaction/${tx._id}`)}
                className={`bg-gray-900 p-4 rounded-xl flex justify-between items-start cursor-pointer ${theme.cardHover} transition`}
              >
                <div>
                  <p className="font-semibold text-white flex items-center gap-2">
                    {tx.title || "Transaction"}
                    {tx.country && (
                      <span className="text-xs">
                        {tx.country === "GH" ? "🇬🇭" : "🇳🇬"}
                      </span>
                    )}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {new Date(tx.createdAt).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {tx.bank_name || ""} {tx.account_number ? `• ${tx.account_number}` : ""}
                  </p>
                </div>

                <div className="text-right">
                  <p
                    className={`font-bold ${
                      tx.status === "failed"
                        ? "text-red-400"
                        : tx.status === "confirmed"
                        ? theme.statusConfirmed
                        : "text-yellow-400"
                    }`}
                  >
                    {txSymbol}{Number(tx.amount || 0).toLocaleString()}
                  </p>
                  <p className="text-gray-400 text-sm capitalize">{tx.status}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-gray-400 mt-6">
          {theme.emptyMsg}
        </p>
      )}
    </div>
  );
};

export default OpayHistoryPage;
